import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Bookmark, ChevronDown, Percent, BarChart, FileText } from 'lucide-react';
import BookmarksDrawer from './BookmarksDrawer';
import SearchBar from './SearchBar';
import { internalHref } from '../utils/internalHref';

const categories = [
  {
    title: "Car Loan Calculators",
    items: [
      { name: "Car Loan Repayment Calculator", path: "/car-loan-calculator" },
      { name: "Car Loan EMI Calculator", path: "/car-loan-emi-calculator" },
      { name: "Car Loan Interest Calculator", path: "/car-loan-interest-calculator" },
      { name: "Car Loan Amortization Calculator", path: "/car-loan-amortization-calculator" },
      { name: "Early Payoff Calculator", path: "/early-payoff-calculator" },
      { name: "Vehicle Stamp Duty Calculator", path: "/stamp-duty-calculator" },
      { name: "Lease vs Buy Calculator", path: "/lease-vs-buy-calculator" },
      { name: "4WD Loan Calculator", path: "/4wd-loan-calculator" },
      { name: "Ute & Truck Loan Calculator", path: "/ute-truck-loan-calculator" },
      { name: "Farm Vehicle Finance Calculator", path: "/farm-vehicle-finance-calculator" },
      { name: "Military Veteran Loan Calculator", path: "/military-veteran-loan-calculator" },
      { name: "Military Relocation Loan Calculator", path: "/military-relocation-loan-calculator" },
      { name: "Comparison Calculator", path: "/car-loan-calculator" },
      { name: "Balloon Payment Calculator", path: "/balloon-payment-calculator" },
      { name: "Novated Lease Calculator", path: "/novated-lease-calculator" },
      { name: "Refinance Calculator", path: "/car-refinance-calculator" },
      { name: "Chattel Mortgage Calculator", path: "/chattel-mortgage-calculator" },
      { name: "Car Loan Payout Calculator", path: "/car-loan-payout-calculator" },
      { name: "Luxury Car Tax Calculator", path: "/luxury-car-tax-calculator" },
      { name: "Affordability Calculator", path: "/car-loan-calculator" }
    ]
  },
  {
    title: "Resources",
    items: [
      { name: "Blog", path: "/blog" },
      { name: "About", path: "/about" },
      { name: "Contact", path: "/contact" },
      { name: "Privacy Policy", path: "/privacy" },
      { name: "Terms of Service", path: "/terms" }
    ]
  }
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBookmarksOpen, setIsBookmarksOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <nav className="bg-blue-600">
      <div className="container mx-auto px-4">
        {/* Logo Section */}
        <div className="flex justify-center py-4">
          <Link to="/" className="flex items-center">
            <div className="bg-white p-2 rounded-lg">
              <img 
                src="/assets/Car loan repayment calculator logo website.png" 
                alt="Car Loan Repayment Calculator Logo" 
                className="h-12"
              />
            </div>
          </Link>
        </div>

        {/* Navigation Section */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center space-x-4">
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-4">
              {categories.map((category) => (
                <div
                  key={category.title}
                  className="relative group"
                  onMouseEnter={() => setActiveCategory(category.title)}
                  onMouseLeave={() => setActiveCategory(null)}
                >
                  <button className="flex items-center space-x-1 text-white hover:text-blue-200 transition py-2">
                    <span>{category.title}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {/* Rendered unconditionally and hidden with CSS, never mounted
                      conditionally: this list previously existed only while
                      `activeCategory` matched, so the prerendered HTML that Googlebot
                      fetches contained ZERO navigation links. The Footer fix of
                      2026-08-19 routed around it, but any route added here and not
                      there would still be born with no crawlable inbound link. */}
                  <div
                    className={`absolute left-0 mt-1 w-64 bg-white rounded-lg shadow-lg text-gray-800 z-50 ${
                      activeCategory === category.title ? 'block' : 'hidden'
                    }`}
                    aria-hidden={activeCategory !== category.title}
                  >
                    <div className="py-2">
                      {category.items.map((item) => (
                        <Link
                          key={item.path}
                          to={internalHref(item.path)}
                          className="block px-4 py-2 hover:bg-blue-50 transition"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden md:block">
              <SearchBar />
            </div>
            
            <button
              onClick={() => setIsBookmarksOpen(true)}
              className="text-white hover:text-blue-200 transition flex items-center gap-2"
            >
              <Bookmark className="h-5 w-5" />
              <span className="hidden md:inline">Bookmarks</span>
            </button>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-white"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="lg:hidden px-4 pb-4">
          <SearchBar />
        </div>
      </div>

      {/* Mobile Menu -- same rule as the desktop dropdown above: always in the DOM,
          visibility driven by CSS, so the links survive into the prerendered HTML. */}
      <div className={`lg:hidden bg-blue-700 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1">
          {categories.map((category) => (
            <div key={category.title} className="space-y-1">
              <div className="px-3 py-2 text-blue-200 font-semibold">
                {category.title}
              </div>
              {category.items.map((item) => (
                <Link
                  key={item.path}
                  to={internalHref(item.path)}
                  className="block px-3 py-2 text-blue-100 hover:bg-blue-800 rounded"
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>

      <BookmarksDrawer
        isOpen={isBookmarksOpen}
        onClose={() => setIsBookmarksOpen(false)}
      />
    </nav>
  );
};

export default Navbar;